import type { DeskDetail } from '@deskworks/shared-types';
import { useNavigate } from '@tanstack/react-router';
import { Copy } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useToast } from '@/lib/toast';

/**
 * Clones a desk in its workspace (docs/features/desk-clone.md), e.g. the SEO desk for another language: everything
 * that makes up the desk is copied, its work is not. What should be different goes to the new desk's Lead, which
 * adapts the copy in a chat; the person lands in that chat.
 */
export function CloneDeskDialog({ desk, open, onOpenChange }: { desk: DeskDetail; open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(t('{desk} (copy)', { desk: desk.name }));
    setPrompt('');
  }, [open, desk.name]);

  const clone = useApiMutation(() => api.desks.clone(desk.id, { name: name.trim(), prompt: prompt.trim() || undefined }), {
    errorTitle: t('The desk was not cloned'),
    invalidate: [qk.desks(desk.workspaceId), qk.overview(desk.workspaceId), qk.agents(desk.workspaceId)],
    onSuccess: (result) => {
      onOpenChange(false);
      for (const todo of result.todos) toast.error(t('Not everything was copied'), todo);
      void navigate({
        to: '/w/$workspaceId/desks/$deskId',
        params: { workspaceId: desk.workspaceId, deskId: result.desk.id },
        search: result.conversationId ? { tab: 'chat', chat: result.conversationId } : {},
      });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && !clone.isPending) clone.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="contents">
          <DialogHeader>
            <DialogTitle>{t('Clone desk')}</DialogTitle>
            <DialogDescription>
              {t(
                'A new desk in this workspace with everything that makes up “{desk}”: settings, agents (as copies), connections and tool permissions, goals and metrics, guidelines, playbooks, desk skills, Work Item types, triggers and files. Not its work: no Work Items, chats or reports. The triggers start off.',
                { desk: desk.name },
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <Field label={t('Name of the new desk')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus required maxLength={200} />
            </Field>
            <Field
              label={t('What should be different?')}
              hint={t('Optional. The new desk’s Lead adapts the copy to it in a chat with you: guidelines, playbooks, agents, triggers. Without it the copy stays as it is.')}
            >
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder={t('e.g. “In English for sellrz.com, UK market. Keywords from the UK, British spelling.”')}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={clone.isPending} disabled={!name.trim()}>
              {!clone.isPending && <Copy />} {t('Clone')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
